// src/components/account/NotificationBell.tsx

import React, { useEffect, useState } from 'react';
import { Badge, Button, Drawer, List, Typography, Tag, Spin, Space, message } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import InfiniteScroll from 'react-infinite-scroll-component'; // Install this library
import {
  fetchNotifications,
  fetchUnreadNotifications,
  markAllAsRead,
  markAsRead,
} from './notification.service';
import { NotificationItem } from '../../types';

const { Text } = Typography;

// Styled components
const NotificationButton = styled(Button)`
  border: none;
  background: transparent;
  font-size: 1.5rem;
  position: relative;
`;

const NotificationListItem = styled(List.Item)<{ isRead: boolean }>`
  background-color: ${({ isRead }) => (isRead ? '#fff' : '#e6f7ff')};
  border-radius: 8px;
  margin-bottom: 0.5rem;
  padding: 1rem;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #f0f0f0;
  }
`;

interface NotificationsDrawerProps {
  visible: boolean;
  onClose: () => void;
}

const NotificationsDrawer: React.FC<NotificationsDrawerProps> = ({ visible, onClose }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [markingAll, setMarkingAll] = useState<boolean>(false);

  useEffect(() => {
    if (visible) {
      // Reset state when drawer opens
      setNotifications([]);
      setPage(1);
      setHasMore(true);
      loadNotifications(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const loadNotifications = async (currentPage: number) => {
    try {
      const { data, total, lastPage } = await fetchNotifications(currentPage, limit);
      setNotifications((prev) => [...prev, ...data]);
      if (currentPage >= lastPage) {
        setHasMore(false);
      }
    } catch (error: any) {
      console.error(error);
      message.error('Failed to load notifications.');
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    loadNotifications(nextPage);
    setPage(nextPage);
  };

  const handleMarkAllAsRead = async () => {
    setMarkingAll(true);
    try {
      await markAllAsRead();
      setNotifications((prev) => prev.map((notif) => ({ ...notif, isRead: true })));
      message.success('All notifications marked as read.');
    } catch (error: any) {
      console.error(error);
      message.error('Failed to mark all as read.');
    } finally {
      setMarkingAll(false);
    }
  };

  const handleMarkAsRead = async (id: number) => {
    try {
      await markAsRead(id);
      setNotifications((prev) =>
        prev.map((notif) => (notif.id === id ? { ...notif, isRead: true } : notif))
      );
      message.success('Notification marked as read.');
    } catch (error: any) {
      console.error(error);
      message.error('Failed to mark as read.');
    }
  };

  return (
    <Drawer
      title="Notifications"
      placement="right"
      onClose={onClose}
      visible={visible}
      width={400}
      bodyStyle={{ padding: '0' }}
      footer={
        hasMore ? (
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <Button onClick={handleLoadMore} loading={false}>
              Load More
            </Button>
          </div>
        ) : null
      }
    >
      <div id="scrollableDiv" style={{ height: '80vh', overflow: 'auto', padding: '1rem' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            type="link"
            onClick={handleMarkAllAsRead}
            disabled={markingAll || notifications.every((notif) => notif.isRead)}
            style={{ alignSelf: 'flex-end' }}
          >
            Mark All as Read
          </Button>
          <InfiniteScroll
            dataLength={notifications.length}
            next={handleLoadMore}
            hasMore={hasMore}
            loader={
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <Spin />
              </div>
            }
            endMessage={
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <Text type="secondary">No more notifications</Text>
              </div>
            }
            scrollableTarget="scrollableDiv"
          >
            <List
              dataSource={notifications}
              renderItem={(item) => (
                <NotificationListItem key={item.id} isRead={item.isRead}>
                  <div style={{padding: '0 10px'}}> <List.Item.Meta
                    title={
                      <Space >
                        <Tag color={item.type === 'info' ? 'blue' : item.type === 'warning' ? 'orange' : item.type === 'error' ? 'red' : 'green'}>
                          {item.type.toUpperCase()}
                        </Tag>
                        <Space direction="horizontal" size="small"><Text strong>{item.title}</Text></Space>
                        
                      </Space>
                    }
                    description={<Text>{item.description}</Text>}
                  />
                  {!item.isRead && (
                    <Button type="link" onClick={() => handleMarkAsRead(item.id)}>
                      Mark as Read
                    </Button>
                  )}
                  <Text type="secondary" style={{ fontSize: '0.8rem' }}>
                    {new Date(item.createdAt).toLocaleString()}
                  </Text>
                  </div>
                </NotificationListItem>
              )}
            />
          </InfiniteScroll>
        </Space>
      </div>
    </Drawer>
  );
};

const NotificationBell: React.FC = () => {
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  const updateUnreadCount = async () => {
    try {
      const unreadNotifications = await fetchUnreadNotifications();
      setUnreadCount(unreadNotifications.length);
    } catch (error: any) {
      console.error(error);
    }
  };

  useEffect(() => {
    updateUnreadCount();
    // Optionally, set up polling or WebSocket listeners for real-time updates
    const interval = setInterval(() => {
      updateUnreadCount();
    }, 60000); // Refresh every 60 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Badge count={unreadCount} overflowCount={99}>
        <NotificationButton
          icon={<BellOutlined style={{ fontSize: '1.2rem', color: '#3b82f6' }} />}
          onClick={() => setDrawerVisible(true)}
        />
      </Badge>
      <NotificationsDrawer visible={drawerVisible} onClose={() => setDrawerVisible(false)} />
    </>
  );
};

export default NotificationBell;
